export type PipelineStage = {
  id: string;
  title: string;
  description: string;
  mode: 'sequential' | 'parallel' | 'conditional';
  enabled: boolean;
  dependsOn: string[];
};

export type SystemPipelineDefinition = {
  version: 5;
  runtime: 'submission-enrichment';
  parameters: Record<string, number | string | boolean | number[]>;
  stages: PipelineStage[];
};

export type SystemPipeline = {
  id: number;
  key: string;
  name: string;
  description: string;
  triggerKey: string;
  enabled: boolean;
  revision: number;
  definition: SystemPipelineDefinition;
};

const stage = (id: string, title: string, dependsOn: string[] = [], mode: PipelineStage['mode'] = 'sequential'): PipelineStage => ({
  id, title, dependsOn, description: '', mode, enabled: true,
});

export const DEFAULT_SUBMISSION_PIPELINE: SystemPipelineDefinition = {
  version: 5,
  runtime: 'submission-enrichment',
  parameters: {
    modelConcurrency: 4,
    llmContextTokens: 4_096,
    llmCharsPerToken: 3,
    llmInstructionReserveTokens: 550,
    llmResponseReserveTokens: 900,
    embeddingNoteChars: 7_000,
    summaryChars: 2_500,
    relationWindowNodes: 4,
    clusterMaxGroupSize: 10,
    clusterNextLevelAt: 9,
    clusterLeafThreshold: 0.72,
    clusterParentThreshold: 0.66,
  },
  stages: [
    stage('impulse', 'Импульс и документы'),
    stage('semantic_config', 'Контекст pipeline', ['impulse']),
    stage('decompose_entry', 'Декомпозиция ввода', ['semantic_config']),
    stage('persist_nodes', 'Рабочие узлы', ['decompose_entry']),
    stage('content_vectors_summary', 'Embeddings + summary', ['persist_nodes'], 'parallel'),
    stage('semantic_extractions', 'Смысловые извлечения', ['persist_nodes'], 'parallel'),
    stage('explanation_relations', 'Объясняющие связи', ['semantic_extractions']),
    stage('semantic_clusters', 'Кластеризация', ['content_vectors_summary', 'explanation_relations']),
    stage('complete', 'Готово', ['semantic_clusters']),
  ],
};

export function submissionPipeline(pipelines?: SystemPipeline[]) {
  return pipelines?.find((item) => item.enabled && item.triggerKey === 'capture.submit')?.definition || DEFAULT_SUBMISSION_PIPELINE;
}

export function stageEnabled(definition: SystemPipelineDefinition, id: string) {
  return definition.stages.find((item) => item.id === id)?.enabled !== false;
}

export function numericParameter(definition: SystemPipelineDefinition, key: string, fallback: number, min: number, max: number) {
  const value = Number(definition.parameters[key]);
  return Number.isFinite(value) ? Math.min(max, Math.max(min, value)) : fallback;
}

export function processingCharacterLimits(definition: SystemPipelineDefinition) {
  const embeddingChars = numericParameter(definition, 'embeddingNoteChars', 7_000, 1_000, 100_000);
  const contextTokens = numericParameter(definition, 'llmContextTokens', 4_096, 1_024, 1_000_000);
  const charsPerToken = numericParameter(definition, 'llmCharsPerToken', 3, 1, 8);
  const instructionTokens = numericParameter(definition, 'llmInstructionReserveTokens', 550, 100, contextTokens - 1);
  const responseTokens = numericParameter(definition, 'llmResponseReserveTokens', 900, 100, contextTokens - instructionTokens);
  const legacyContextChars = numericParameter(definition, 'llmContextChars', Number.MAX_SAFE_INTEGER, 1_000, 10_000_000);
  const llmInputChars = Math.max(500, Math.floor((contextTokens - instructionTokens - responseTokens) * charsPerToken));
  const noteChars = Math.min(embeddingChars, llmInputChars, legacyContextChars);
  const requestedSummaryChars = numericParameter(definition, 'summaryChars', 2_500, 250, 100_000);
  return {
    noteChars,
    llmInputChars: Math.min(llmInputChars, legacyContextChars),
    summaryChars: Math.min(noteChars, requestedSummaryChars, Math.floor(responseTokens * charsPerToken)),
  };
}
